import { useGet, usePost, usePut, useDelete } from '~/composables/fetch'
import type { CompetitiveMatchMode, PlayingSide } from '~/types/models'

const BASE = '/competitive-matches'
const NOTIF = '/game-notifications'

// Every mutation below is error-handled by its caller, so suppress the generic
// handler in fetch.ts that would otherwise dump the raw server body as a toast.
// Built per call because the fetch helpers write auth headers into the object.
const SILENT = () => ({ headers: { 'no-error': true } })

// The API answers with a plain English string (response.badRequest('...')).
const SERVER_MESSAGES: Record<string, string> = {
  'Match is already at capacity': 'ظرفیت مچ تکمیل است',
  'User already has a pending or accepted slot': 'این کاربر قبلاً به این مچ دعوت شده است',
  'Cannot invite yourself': 'نمی‌توانید خودتان را دعوت کنید',
  'Only the host can invite players': 'فقط میزبان می‌تواند بازیکن دعوت کند',
  'Only the host can edit this match': 'فقط میزبان می‌تواند این مچ را ویرایش کند',
  'Only the host can cancel this match': 'فقط میزبان می‌تواند این مچ را لغو کند',
  'Match is cancelled': 'این مچ لغو شده است',
  'Cannot edit a cancelled match': 'مچ لغو شده قابل ویرایش نیست',
  'Match not found': 'مچ یافت نشد',
  'This match is private': 'این مچ خصوصی است',
  'You are already the host': 'شما میزبان این مچ هستید',
  'A court and time are required': 'زمین و زمان مچ را مشخص کنید',
  'A reservation is required': 'یک رزرو انتخاب کنید',
  'Club name is required': 'نام باشگاه را وارد کنید',
  'Match date is required': 'تاریخ مچ را انتخاب کنید',
  'Match time is required': 'ساعت مچ را انتخاب کنید',
  'Level assessment required': 'برای ادامه ابتدا تعیین سطح را انجام دهید',
  SIDE_FULL: 'ظرفیت این تیم تکمیل است',
  SLOT_TAKEN: 'این جایگاه قبلاً انتخاب شده است',
  MATCH_FULL: 'ظرفیت مچ تکمیل است',
  INVALID_SIDE: 'تیم انتخاب‌شده معتبر نیست',
  INVALID_SLOT: 'جایگاه انتخاب‌شده معتبر نیست',
  PLAYING_SIDE_FULL: 'این سمت پر شده است',
  INVALID_PLAYING_SIDE: 'سمت بازی انتخاب‌شده معتبر نیست',
  INVALID_MATCH_MODE: 'نوع مچ انتخاب‌شده معتبر نیست',
  PRIVATE_MATCH_FRIENDLY_ONLY: 'مچ خصوصی فقط دوستانه است',
  MATCH_MODE_LOCKED: 'پس از ثبت نتیجه، نوع مچ قابل تغییر نیست',
  MATCH_DEACTIVATED: 'این مچ غیرفعال شده است',
  CANCEL_WINDOW_CLOSED: 'کمتر از ۲۴ ساعت به شروع مچ مانده و امکان لغو وجود ندارد',
  MATCH_ALREADY_PLAYED: 'مچ انجام شده و قابل لغو نیست',
}

export const matchErrorMessage = (error: any, fallback: string) => {
  const data = error?.data
  const raw = (typeof data === 'string' ? data : data?.code || data?.message || data?.error || '').trim()
  return SERVER_MESSAGES[raw] || fallback
}

// Matches
export const getCMatches = async (params: Record<string, any> = {}) => {
  const { result } = await useGet<any>(`${BASE}`, { params })
  return result || { data: [], total: 0, page: 1, perPage: 20 }
}

export const getCMatch = async (id: string, params: Record<string, any> = {}) => {
  const { result } = await useGet<any>(`${BASE}/${id}`, {params})
  return result || null
}

export const createCMatch = async (body: Record<string, any> & {
  hostPlayingSide: PlayingSide
  matchMode: CompetitiveMatchMode
}) => {
  const { result, error } = await usePost<any, any>(`${BASE}`, body, SILENT())
  return { result, error }
}

export const updateCMatch = async (id: string, body: Record<string, any> & { matchMode?: CompetitiveMatchMode }) => {
  const { result, error } = await usePut<any, any>(`${BASE}/${id}`, body, SILENT())
  return { result, error }
}

export const cancelCMatch = async (id: string) => {
  const { result, error } = await useDelete<any>(`${BASE}/${id}`, SILENT())
  return { result, error }
}

// Participants
export const inviteToMatch = async (matchId: string, userId: string, slotIndex?: number) => {
  const { result, error } = await usePost<any, any>(`${BASE}/${matchId}/invites`, { userId, slotIndex }, SILENT())
  return { result, error }
}

export const requestToJoinMatch = async (
  matchId: string,
  selection: { playingSide: PlayingSide; slotIndex?: number; invite?: string }
) => {
  const { result, error } = await usePost<any, any>(`${BASE}/${matchId}/join-requests`, selection, SILENT())
  return { result, error }
}

export const getMatchInviteLink = async (matchId: string, regenerate = false) => {
  const { result, error } = await usePost<any, any>(
    `${BASE}/${matchId}/invite-link`,
    regenerate ? {regenerate: true} : {},
    SILENT()
  )
  return {result, error}
}

export const getMatchInvitePreview = async (code: string) => {
  const {result, error} = await useGet<any>(
    `${BASE}/invite/${encodeURIComponent(code)}/preview`,
    SILENT()
  )
  return {result, error}
}

export const resolveMatchInvite = async (code: string) => {
  const {result, error} = await useGet<any>(`${BASE}/invite/${encodeURIComponent(code)}`)
  return {result, error}
}

export const acceptParticipant = async (matchId: string, participantId: string) => {
  const { result, error } = await usePost<any, any>(
    `${BASE}/${matchId}/participants/${participantId}/accept`,
    {},
    SILENT()
  )
  return { result, error }
}

export const rejectParticipant = async (matchId: string, participantId: string) => {
  const { result, error } = await usePost<any, any>(
    `${BASE}/${matchId}/participants/${participantId}/reject`,
    {},
    SILENT()
  )
  return { result, error }
}

export const leaveMatch = async (matchId: string, participantId: string) => {
  const { result, error } = await useDelete<any>(
    `${BASE}/${matchId}/participants/${participantId}`,
    SILENT()
  )
  return { result, error }
}

export const getJoinRequests = async (matchId: string, params: Record<string, any> = {}) => {
  const { result } = await useGet<any>(`${BASE}/${matchId}/requests`, { params })
  return result || { data: [], total: 0 }
}

// §2.2 gaps: external players, reserve promotion, replacement.
export const addExternalPlayer = async (
  matchId: string,
  payload: { externalName: string; externalContact?: string; isReserve?: boolean; slotIndex?: number }
) => {
  const { result, error } = await usePost<any, any>(`${BASE}/${matchId}/external-players`, payload)
  return { result, error }
}

export const promoteReserve = async (matchId: string, participantId: string) => {
  const { result, error } = await usePost<any, any>(`${BASE}/${matchId}/participants/${participantId}/promote`, {})
  return { result, error }
}

export const replaceParticipant = async (
  matchId: string,
  participantId: string,
  payload: { userId?: string; externalName?: string; externalContact?: string }
) => {
  const { result, error } = await usePost<any, any>(`${BASE}/${matchId}/participants/${participantId}/replace`, payload)
  return { result, error }
}

// Notifications
export const getGameNotifications = async (params: Record<string, any> = {}) => {
  const { result } = await useGet<any>(`${NOTIF}`, { params })
  return result || { data: [], total: 0 }
}

export const getUnreadCount = async () => {
  const { result } = await useGet<any>(`${NOTIF}/unread-count`)
  return result?.count || 0
}

export const markNotifRead = async (id: string) => {
  const { result, error } = await usePost<any, any>(`${NOTIF}/${id}/read`, {})
  return { result, error }
}

export const markAllNotifsRead = async () => {
  const { result, error } = await usePost<any, any>(`${NOTIF}/read-all`, {})
  return { result, error }
}

// User search (by name or phone — phone never returned)
// Normalizes Persian/Arabic-Indic digits (e.g. ۰۹۱۲...) to English so phone
// searches work regardless of keyboard/locale (VIVA-50). Reuses the p2e
// helper from composables/global.ts introduced for VIVA-30.
export const searchUsers = async (q: string, page = 1, perPage = 20) => {
  const { result } = await useGet<any>(`/users/search`, { params: { q: p2e(q.trim()), page, perPage } })
  // The endpoint currently returns an array; accept a paginated envelope too
  // so the search sheet remains compatible with API deployments that wrap data.
  return Array.isArray(result) ? result : (Array.isArray(result?.data) ? result.data : [])
}
